import React, { lazy } from 'react';
import { COOKIE_AUTH_DETAILS } from 'globalconfig/constants/keys';
import { getCookie } from 'globalservices/cookie';
import { getFilterLocaleValues, isInsuracePortalUser, isdvirOnly } from 'globalutils/fmUtils';
import InfoIcon from '@material-ui/icons/Info';
import { Field } from 'formik';
import Tooltip from 'rc-tooltip';
import underscore from 'underscore';
import 'rc-tooltip/assets/bootstrap.css';
import { useTranslation } from 'react-i18next';
import { checkServiceProvider } from './serviceProviderConfig';
import { hasAnyValueMatch } from 'globalutils/underscoreUtil';

const layout = window.$environment?.CURRENT_LAYOUT;
const Label = lazy(() => import(`globalcomponents/UI/FormLabel/${layout}`));
const SubHeader = lazy(() => import(`globalcomponents/SubHeader/${layout}`));
const Select = lazy(() => import(`globalcomponents/UI/Select/${layout}`));
const FormStyle = require(`globalcomponents/UI/Form/${layout}/serverStyle`).default;

const Insurance = ({ values, handleEdit, Styled, allCycleRules, allCycleExceptions, allVehicleTypes, eventTypes }) => {
  const token = getCookie(COOKIE_AUTH_DETAILS);
  const isDvirOnlyFlagEnabled = isdvirOnly();
  const i18n = useTranslation();

  return (
    <Styled.InsuranceCompanyWrapper>
      {!isDvirOnlyFlagEnabled && token?.roles?.includes('FLMANAGER') && token?.enterpriseFeatureDto?.cycleRuleEnabled && (
        <div className="cycleruleSection">
          <SubHeader text={i18n.t('common.cycleRule')} className="subheaderSection companyTitle" variant="h2" />
          <FormStyle.FieldsWrapper>
            <Label htmlFor="assignedCycleRules" variant="body1">
              {i18n.t('common.cycleRule')}
            </Label>
            <FormStyle.TextWrapper>
              <Field
                name="assignedCycleRules"
                id="assignedCycleRules"
                onChange={(event) => {
                  if (
                    !event ||
                    !event.length ||
                    (event.length && !hasAnyValueMatch(event, ['USA 60h/7 days', 'USA 70h/8 days', 'Texas 70h/7 day']))
                  ) {
                    handleEdit('', {
                      field: 'assignedCycleExceptions',
                    });
                    handleEdit(allVehicleTypes[0], {
                      field: 'assignedVehicleType',
                    });
                  }
                  if (
                    event &&
                    event.length &&
                    hasAnyValueMatch(event, ['USA 60h/7 days', 'USA 70h/8 days']) &&
                    hasAnyValueMatch(event, [
                      'Local Radius',
                      'Texas 70h/7 day',
                      'Canada 70h/7 day South',
                      'Canada 120h/14 day South',
                      'Canada 70h/7 day North',
                      'Canada 120h/14 day North',
                      'Mexico',
                    ])
                  ) {
                    handleEdit(allVehicleTypes[0], {
                      field: 'assignedVehicleType',
                    });
                  }
                  if (!event || !event.length) {
                    handleEdit(allVehicleTypes[0], {
                      field: 'assignedVehicleType',
                    });
                  }
                  handleEdit(event, { field: 'assignedCycleRules' });
                  handleEdit('', { field: 'defaultCycleRule' });
                }}
                as={Select}
                placeholder={i18n.t('common.pleaseSelect')}
                value={getFilterLocaleValues(values['assignedCycleRules'], i18n)}
                suggestions={getFilterLocaleValues(allCycleRules, i18n)}
                isClearable
                isMulti
              />
            </FormStyle.TextWrapper>
          </FormStyle.FieldsWrapper>

          <FormStyle.FieldsWrapper>
            <Label htmlFor="assignedCycleExceptions" variant="body1">
              {i18n.t('companyData.exceptions')}
            </Label>
            <FormStyle.TextWrapper>
              <Field
                name="assignedCycleExceptions"
                id="assignedCycleExceptions"
                onChange={(event) => {
                  handleEdit(event, {
                    field: 'assignedCycleExceptions',
                  });
                }}
                as={Select}
                placeholder={i18n.t('common.pleaseSelect')}
                value={getFilterLocaleValues(values['assignedCycleExceptions'], i18n)}
                suggestions={getFilterLocaleValues(allCycleExceptions, i18n)}
                isDisabled={
                  !values?.assignedCycleRules ||
                  !values?.assignedCycleRules.length ||
                  (values?.assignedCycleRules.length &&
                    !hasAnyValueMatch(values?.assignedCycleRule, ['USA 60h/7 days', 'USA 70h/8 days', 'Texas 70h/7 day']))
                }
                isClearable
                isMulti
              />
            </FormStyle.TextWrapper>
          </FormStyle.FieldsWrapper>

          <FormStyle.FieldsWrapper>
            <Label htmlFor="defaultCycleRule" variant="body1">
              {i18n.t('companyData.defaultCycleRule')}
            </Label>
            <FormStyle.TextWrapper>
              <Field
                name="defaultCycleRule"
                id="defaultCycleRule"
                onChange={(event) => handleEdit(event, { field: 'defaultCycleRule' })}
                as={Select}
                placeholder={i18n.t('common.pleaseSelect')}
                value={getFilterLocaleValues([values['defaultCycleRule']], i18n)}
                suggestions={getFilterLocaleValues(values['assignedCycleRules'] || [], i18n)}
              />
            </FormStyle.TextWrapper>
          </FormStyle.FieldsWrapper>

          <FormStyle.FieldsWrapper>
            <Label htmlFor="assignedVehicleType" variant="body1">
              {i18n.t('companyData.vehicleType')}
            </Label>
            <FormStyle.TextWrapper>
              <Field
                name="assignedVehicleType"
                id="assignedVehicleType"
                onChange={(event) => handleEdit(event, { field: 'assignedVehicleType' })}
                as={Select}
                placeholder={i18n.t('common.pleaseSelect')}
                value={getFilterLocaleValues([values['assignedVehicleType']], i18n)}
                suggestions={getFilterLocaleValues(
                  values?.assignedCycleRules &&
                    values?.assignedCycleRules.length &&
                    (hasAnyValueMatch(values?.assignedCycleRules, ['USA 60h/7 days', 'USA 70h/8 days']) ||
                      underscore.pluck(values?.assignedCycleRules, 'value').find((rule) => rule === 'Local Radius') !==
                      undefined) &&
                    !hasAnyValueMatch(values?.assignedCycleRules, [
                      'Texas 70h/7 day',
                      'Canada 70h/7 day South',
                      'Canada 120h/14 day South',
                      'Canada 70h/7 day North',
                      'Canada 120h/14 day North',
                      'Mexico',
                    ])
                    ? allVehicleTypes
                    : [{ label: ' Property', value: 'Property' }],
                  i18n
                )}
              />
            </FormStyle.TextWrapper>
          </FormStyle.FieldsWrapper>
        </div>
      )}
      {!isInsuracePortalUser() && checkServiceProvider('fields_with_feature', window.$environment?.SERVICE_PROVIDER, false) ? (
        <div className="cycleruleSection">
          <SubHeader text={i18n.t('common.configuration')} className="subheaderSection companyTitle" variant="h2" />
          <FormStyle.FieldsWrapper className="spnocycle">
            <Tooltip
              mouseEnterDelay={0.1}
              placement="top"
              overlayStyle={{ maxWidth: '150px', opacity: 1 }}
              overlay={<Label variant="body1">{i18n.t('common.alertToEmailInfo')}</Label>}
            >
              <Label htmlFor="alertsToEmail" variant="body1">
                <div style={{ display: 'flex' }}>
                  {i18n.t('common.emailAlerts')}
                  <InfoIcon fontSize="small" color="info" />
                </div>
              </Label>
            </Tooltip>
            <FormStyle.TextWrapper className="userForm">
              <Field
                name="alertsToEmail"
                id="alertsToEmail"
                onChange={(event) => {
                  handleEdit(event, { field: 'alertsToEmail' });
                }}
                as={Select}
                placeholder={i18n.t('common.pleaseSelect')}
                value={getFilterLocaleValues(values['alertsToEmail'], i18n)}
                suggestions={eventTypes}
                isClearable
                isMulti
              />
            </FormStyle.TextWrapper>
          </FormStyle.FieldsWrapper>
        </div>
      ) : (
        <></>
      )}
    </Styled.InsuranceCompanyWrapper>
  );
};

export default Insurance;
